import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material'
import {
  Close,
  Refresh,
  SmartToy,
  CheckCircle,
  Error as ErrorIcon,
  DataObject,
  RadioButtonUnchecked,
} from '@mui/icons-material'
import Markdown from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import { getToken } from '../../services/Main/AuthToken'

const TicketAIInvestigator = ({ open, onClose, ticketId, onComplete }) => {
  const [content, setContent] = useState('')
  const [steps, setSteps] = useState([])
  const [status, setStatus] = useState('idle') // idle, investigating, done, error
  const [error, setError] = useState(null)
  const [meta, setMeta] = useState(null)
  const abortRef = useRef(null)
  const contentRef = useRef(null)

  useEffect(() => {
    if (open && ticketId) {
      investigate()
    }
    return () => {
      if (abortRef.current) abortRef.current.abort()
    }
  }, [open, ticketId])

  // Auto-scroll content as it streams
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight
    }
  }, [content, steps])

  const investigate = useCallback(() => {
    setContent('')
    setSteps([])
    setError(null)
    setMeta(null)
    setStatus('investigating')

    const controller = new AbortController()
    abortRef.current = controller

    const token = getToken()
    const baseURL = process.env.REACT_APP_API_URL || ''

    fetch(`${baseURL}/tickets/${ticketId}/ai-investigate/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    })
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        function processChunk() {
          return reader.read().then(({ done, value }) => {
            if (done) {
              setStatus(prev => prev === 'investigating' ? 'done' : prev)
              return
            }

            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() // keep incomplete line

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6))
                  handleSSEEvent(data)
                } catch (e) {
                  // ignore parse errors
                }
              }
            }

            return processChunk()
          })
        }

        return processChunk()
      })
      .catch(err => {
        if (err.name === 'AbortError') return
        setError(err.message || 'Erro ao investigar o ticket.')
        setStatus('error')
      })
  }, [ticketId])

  const handleSSEEvent = (data) => {
    switch (data.type) {
      case 'start':
        setMeta({ model: data.model, investigation_id: data.investigation_id })
        break

      case 'step':
        setSteps(prev => [...prev, {
          id: data.id,
          order: data.order,
          title: data.title,
          status: data.status,
          tool: data.tool,
          duration_ms: data.duration_ms,
          description: data.description,
        }])
        break

      case 'content':
        setContent(prev => prev + data.text)
        break

      case 'done':
        setStatus('done')
        setMeta(prev => ({ ...prev, ...data }))
        if (onComplete) onComplete()
        break

      case 'error':
        setError(data.message)
        setStatus('error')
        break

      default:
        break
    }
  }

  const handleClose = () => {
    if (abortRef.current) abortRef.current.abort()
    onClose()
  }

  const stepStatusIcon = (s) => {
    if (s === 'success') return <CheckCircle sx={{ fontSize: 16, color: '#16A34A' }} />
    if (s === 'failed') return <ErrorIcon sx={{ fontSize: 16, color: '#DC2626' }} />
    if (s === 'no_data') return <RadioButtonUnchecked sx={{ fontSize: 16, color: '#D97706' }} />
    if (s === 'warning') return <ErrorIcon sx={{ fontSize: 16, color: '#D97706' }} />
    return <RadioButtonUnchecked sx={{ fontSize: 16, color: '#94A3B8' }} />
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '85vh' } }}
    >
      <Box sx={{ height: 4, background: 'linear-gradient(90deg, #7C3AED 0%, #A78BFA 50%, #C4B5FD 100%)' }} />
      {status === 'investigating' && <LinearProgress sx={{ height: 2, '& .MuiLinearProgress-bar': { bgcolor: '#7C3AED' } }} />}

      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2, bgcolor: '#EDE9FE',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <SmartToy sx={{ fontSize: 22, color: '#7C3AED' }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Investigação com IA
              {meta?.model && (
                <Chip label={meta.model} size="small" sx={{ ml: 1, height: 20, fontSize: 10, bgcolor: '#EDE9FE', color: '#7C3AED' }} />
              )}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {status === 'investigating' ? 'Analisando...' :
               status === 'done' ? `Concluída em ${meta?.duration}s — ${meta?.total_tokens?.toLocaleString('pt-BR') || 0} tokens` :
               'Análise automática dos dados do ticket'}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent ref={contentRef} sx={{ p: 3, minHeight: 200 }}>
        {/* Steps */}
        {steps.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748B', textTransform: 'uppercase', fontSize: 10 }}>
              Passos da investigação
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {steps.map((step, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                  {stepStatusIcon(step.status)}
                  <Typography variant="body2" sx={{ fontSize: 13, color: '#475569' }}>
                    {step.title}
                  </Typography>
                  {step.tool && (
                    <Chip
                      icon={<DataObject sx={{ fontSize: '12px !important' }} />}
                      label={step.tool}
                      size="small"
                      sx={{ height: 18, fontSize: 10, bgcolor: '#F1F5F9', '& .MuiChip-icon': { ml: 0.3 } }}
                    />
                  )}
                  {step.duration_ms != null && (
                    <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: 10 }}>
                      {step.duration_ms}ms
                    </Typography>
                  )}
                </Box>
              ))}
              {status === 'investigating' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                  <CircularProgress size={14} sx={{ color: '#7C3AED' }} />
                  <Typography variant="body2" sx={{ fontSize: 13, color: '#94A3B8', fontStyle: 'italic' }}>
                    Processando...
                  </Typography>
                </Box>
              )}
            </Box>
            <Divider sx={{ mt: 1.5, mb: 2 }} />
          </Box>
        )}

        {/* Streaming content */}
        {content && (
          <Box sx={{
            '& h3': { fontSize: 16, fontWeight: 700, mt: 2, mb: 1, color: '#1E293B' },
            '& h4': { fontSize: 14, fontWeight: 600, mt: 1.5, mb: 0.5, color: '#334155' },
            '& p': { fontSize: 14, lineHeight: 1.6, mb: 1, color: '#334155' },
            '& ul, & ol': { pl: 2, mb: 1 },
            '& li': { fontSize: 14, mb: 0.5 },
            '& strong': { color: '#1E293B' },
            '& code': { bgcolor: '#F1F5F9', px: 0.5, borderRadius: 0.5, fontSize: 13 },
            '& hr': { border: 'none', borderTop: '1px solid #E2E8F0', my: 2 },
            '& table': { width: '100%', borderCollapse: 'collapse', mb: 2 },
            '& th, & td': { border: '1px solid #E2E8F0', px: 1.5, py: 0.75, fontSize: 13, textAlign: 'left' },
            '& th': { bgcolor: '#F8FAFC', fontWeight: 600 },
          }}>
            <Markdown remarkPlugins={[remarkBreaks]}>
              {content}
            </Markdown>
            {status === 'investigating' && (
              <Box component="span" sx={{
                display: 'inline-block', width: 8, height: 16, bgcolor: '#7C3AED',
                animation: 'blink 1s infinite',
                '@keyframes blink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0 } },
                ml: 0.5, verticalAlign: 'text-bottom', borderRadius: 0.5,
              }} />
            )}
          </Box>
        )}

        {/* Loading without steps yet */}
        {status === 'investigating' && steps.length === 0 && !content && (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6} gap={2}>
            <CircularProgress sx={{ color: '#7C3AED' }} />
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
              Iniciando investigação...
            </Typography>
          </Box>
        )}

        {/* Error */}
        {error && status === 'error' && (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4} gap={2}>
            <Typography variant="body2" sx={{ color: '#DC2626', textAlign: 'center' }}>
              {error}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={investigate}
              sx={{ color: '#7C3AED', borderColor: '#7C3AED', '&:hover': { borderColor: '#6D28D9', bgcolor: '#F5F3FF' } }}
            >
              Tentar novamente
            </Button>
          </Box>
        )}

        {/* Done actions */}
        {status === 'done' && content && (
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={investigate}
              sx={{ color: '#7C3AED', borderColor: '#DDD6FE', textTransform: 'none', '&:hover': { borderColor: '#7C3AED', bgcolor: '#F5F3FF' } }}
            >
              Investigar novamente
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default TicketAIInvestigator
